import axios from 'axios'
import Swal from 'sweetalert2'
import { useI18n } from 'vue-i18n'
import { EXISTING_ACCOUNT_ERROR_CODE } from '../constants/AddressBook'
import type { IErrorResponse } from '../types/AoeTypes'
import type { IExistingAccount, IExistingAccountConfirmation } from '../types/AddressBookTypes'

/**
 * A contact whose email already belongs to an existing customer account gets linked to that account, and invoices
 * and receipts show the account's name. Saving a contact with checkExistingAccount makes the API reject the save
 * when that would happen and return the account's name and email, so the consultant can select it and confirm it is
 * the same person. On confirm the save is sent again with confirmExistingAccount and the contact takes the account's
 * name; the account itself is never renamed.
 */
export function useExistingAccountConfirm() {
  const { t } = useI18n()

  function getExistingAccount(error: unknown): IExistingAccount | null {
    if (!axios.isAxiosError(error)) return null
    const responseStatus = (error.response?.data as IErrorResponse | undefined)?.responseStatus
    if (responseStatus?.errorCode !== EXISTING_ACCOUNT_ERROR_CODE) return null
    return {
      firstName: responseStatus.meta?.firstName ?? '',
      lastName: responseStatus.meta?.lastName ?? '',
      emailAddress: responseStatus.meta?.emailAddress ?? '',
    }
  }

  // Built with textContent rather than an HTML string: the name comes from the account and must not be parsed as markup
  function buildAccountSelection(account: IExistingAccount): HTMLElement {
    const container = document.createElement('div')

    const option = document.createElement('label')
    option.className = 'existing-account-option'

    const radio = document.createElement('input')
    radio.type = 'radio'
    radio.name = 'existingAccount'
    radio.className = 'existing-account-radio'

    const details = document.createElement('span')
    details.className = 'existing-account-details'
    const name = document.createElement('span')
    name.className = 'existing-account-name'
    name.textContent = `${account.firstName} ${account.lastName}`.trim()
    const email = document.createElement('span')
    email.className = 'existing-account-email'
    email.textContent = account.emailAddress
    details.append(name, email)

    option.append(radio, details)

    const note = document.createElement('p')
    note.className = 'existing-account-note'
    note.textContent = t('alerts.ExistingAccountText')

    container.append(option, note)
    return container
  }

  // The consultant must select the account before confirming; otherwise they change the email
  async function confirmExistingAccount(account: IExistingAccount): Promise<boolean> {
    const result = await Swal.fire({
      title: t('alerts.ExistingAccountTitle'),
      html: buildAccountSelection(account),
      showCancelButton: true,
      confirmButtonText: t('alerts.ExistingAccountConfirm'),
      cancelButtonText: t('alerts.ExistingAccountChangeEmail'),
      customClass: { popup: 'existing-account-dialog' },
      didOpen: (popup) => {
        const confirmButton = Swal.getConfirmButton()
        if (confirmButton) confirmButton.disabled = true
        popup.querySelector<HTMLInputElement>('.existing-account-radio')?.addEventListener('change', () => {
          if (confirmButton) confirmButton.disabled = false
        })
      },
    })
    return result.isConfirmed
  }

  /**
   * Runs the save with the existing-account check and, if the email belongs to an existing account, asks the
   * consultant to select and confirm it before running it again. Resolves to null when the consultant chooses to
   * change the email; any other error is rethrown to the caller's existing error handling.
   */
  async function saveWithExistingAccountConfirm<T>(
    save: (confirmation: IExistingAccountConfirmation) => Promise<T>,
  ): Promise<T | null> {
    try {
      return await save({ checkExistingAccount: true, confirmExistingAccount: false })
    } catch (error) {
      const existingAccount = getExistingAccount(error)
      if (!existingAccount) throw error
      if (!(await confirmExistingAccount(existingAccount))) return null
      return await save({ checkExistingAccount: true, confirmExistingAccount: true })
    }
  }

  return {
    saveWithExistingAccountConfirm,
  }
}
